import React, { lazy, Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './theme/tokens.css';
import './theme/components.css';
import './global.css';
import { Analytics } from '@vercel/analytics/react';

// Componentes leves / sempre presentes — import direto
import ErrorBoundary from './Component/ErrorBoundary';
import BlockedGate from './Component/BlockedGate';
import CardapioRedirect from './Component/CardapioRedirect';
import NotFound from './Component/NotFound';
import SupportWidget from './Component/SupportWidget';
import ProtectedRoute from './Component/ProtectedRoute';
import AdminStoreBanner from './Component/AdminStoreBanner';
import OnboardingTour from './Component/OnboardingTour';
import { useTour } from './Confg/TourContext';

// Telas carregadas sob demanda (lazy) — só baixam quando acessadas
const Authentication = lazy(() => import('./screens/Authentication'));
const UserProfile = lazy(() => import('./Confg/UserProfile'));
const Cardapio = lazy(() => import('./screens/Cardapio'));
const CardapioAdmin = lazy(() => import('./screens/Cardapio/cardapio-admin'));
const CardapioIfood = lazy(() => import('./screens/CardapioIfood'));
const Adicionar = lazy(() => import('./screens/Cardapio/adicionar'));
const EditarItem = lazy(() => import('./screens/Cardapio/editar-item'));
const Home = lazy(() => import('./screens/Home'));
const Sobre = lazy(() => import('./screens/sobre'));
const EditarLoja = lazy(() => import('./screens/EditarLoja'));
const LandingPage = lazy(() => import('./screens/LandingPage'));
const LandingPageCourier = lazy(() => import('./screens/LandingPageCourier'));
const Chat = lazy(() => import('./Component/Chat'));
const OrderDetails = lazy(() => import('./screens/OrderDetails'));
const PlatformPlans = lazy(() => import('./screens/PlatformPlans'));
const TodoScreen = lazy(() => import('./screens/TodoScreen'));
const TermsOfUse = lazy(() => import('./screens/TermsOfUse'));
const Privacy = lazy(() => import('./screens/Privacy'));
const CallCourier = lazy(() => import('./screens/CallCourier'));
const CustomerTracking = lazy(() => import('./screens/CustomerTracking'));
const PedidosList = lazy(() => import('./screens/PedidosList'));
const AdminStores = lazy(() => import('./screens/AdminStores'));

// Fallback simples enquanto a tela é baixada
const ScreenLoader = () => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minHeight: '100vh', background: 'var(--background-color)', gap: 16, flexDirection: 'column'
  }}>
    <div style={{
      width: 38, height: 38, borderRadius: '50%',
      border: '4px solid var(--border-global)', borderTopColor: 'var(--button-background)',
      animation: 'appSpin 0.9s linear infinite'
    }} />
    <style>{`@keyframes appSpin { to { transform: rotate(360deg); } }`}</style>
  </div>
);


const App = () => {
  const location = useLocation();
  const { tourSteps, onTourFinish, stopTour } = useTour();
  const isCardapioIfood = location.pathname.toLowerCase().includes("cardapio-ifood") || 
                          location.pathname.toLowerCase().includes("cardapioifood");
  // Telas do CLIENTE final (cardápio público, rastreio, link curto): o chat de suporte
  // é da loja com a Auuii, não faz sentido para quem está pedindo.
  const isClientScreen = /^\/(merchants\/[^/]+\/cardapio|rastreio|c)\b/i.test(location.pathname);

  React.useEffect(() => {
    const savedTheme = localStorage.getItem("theme") || "dark";
    // As cores de cada tema vivem em theme/tokens.css (design system Auuii Painel),
    // selecionadas por [data-theme]. Aqui só trocamos o atributo e limpamos valores
    // inline antigos, que teriam prioridade sobre o CSS.
    const LEGACY_INLINE = [
      '--background-color', '--text-color', '--text-muted', '--bg-card-global',
      '--border-global', '--button-background', '--button-hover-background',
      '--highlight-color', '--shadow-global',
    ];
    const updateThemeVariables = (t) => {
      const root = document.documentElement;
      LEGACY_INLINE.forEach((name) => root.style.removeProperty(name));
      root.setAttribute("data-theme", t === "light" ? "light" : "dark");
    };

    updateThemeVariables(savedTheme);

    const handleStorageChange = (e) => {
      if (e.key === "theme") updateThemeVariables(e.newValue);
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  return (
    <>
      <Analytics />
      
      {/* Controle de visibilidade do chat de suporte da Auuii.
          As regras que escondiam o widget do iFood fora da rota do cardápio saíram
          junto com o próprio widget (removido em 12/09/2026 — ver public/index.html). */}
      <style>{`
        /* Garante que o container do chat da Auuii não apareça onde não deve */
        .support-widget-container,
        [class*="SupportWidget_widgetContainer"],
        [id*="support-widget"] {
           display: ${(isCardapioIfood || isClientScreen) ? 'none' : 'block'} !important;
        }
      `}</style>

      {/* Avisa, em toda tela, quando um admin está operando a loja de outra pessoa */}
      <AdminStoreBanner />

      <SupportWidget />

      {/* Tour de boas-vindas (navega pelas telas; controlado pelo TourContext) */}
      {tourSteps && (
        <OnboardingTour
          steps={tourSteps}
          onFinish={() => {
            if (onTourFinish) onTourFinish();
            stopTour();
          }}
        />
      )}
      {/* ToastContainer para notificações */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* Rotas da aplicação */}
      <ErrorBoundary>
      <Suspense fallback={<ScreenLoader />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/entregador" element={<LandingPageCourier />} />
        <Route path="/Authentication" element={<Authentication />} />
        <Route path="/cardapio-ifood/:merchantId" element={<ProtectedRoute requireMerchant><BlockedGate><CardapioIfood /></BlockedGate></ProtectedRoute>} />
        <Route path="/cardapio-ifood" element={<ProtectedRoute requireMerchant><BlockedGate><CardapioIfood /></BlockedGate></ProtectedRoute>} />
        <Route path="/chat/:chatId" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
        <Route path="/app/plans" element={<ProtectedRoute><PlatformPlans /></ProtectedRoute>} />
        <Route path="/merchants/:userId/pedidos/:orderId" element={<ProtectedRoute requireMerchant><BlockedGate><OrderDetails /></BlockedGate></ProtectedRoute>} />
        <Route path="/c/:alias" element={<CardapioRedirect />} />
        <Route path="/Home" element={<ProtectedRoute requireMerchant><Home /></ProtectedRoute>} />
        <Route path="/sobre" element={<Sobre />} />
        <Route path="/editar-loja/:uid" element={<ProtectedRoute requireMerchant><BlockedGate><EditarLoja /></BlockedGate></ProtectedRoute>} />
        <Route path="/cardapio-admin" element={<ProtectedRoute requireMerchant><BlockedGate><CardapioAdmin /></BlockedGate></ProtectedRoute>} />
        <Route path="/merchants/:uid/adicionar-item" element={<ProtectedRoute requireMerchant><BlockedGate><Adicionar /></BlockedGate></ProtectedRoute>} />
        <Route path="/merchants/:restauranteId/cardapio" element={<Cardapio />} />
        <Route path="/editar-item/:uid/:id" element={<ProtectedRoute requireMerchant><BlockedGate><EditarItem /></BlockedGate></ProtectedRoute>} />
        <Route path="/Perfil" element={<ProtectedRoute><UserProfile /></ProtectedRoute>} />
        <Route path="/not-found" element={<NotFound />} />
        <Route path="/TodoScreen" element={<ProtectedRoute><BlockedGate><TodoScreen /></BlockedGate></ProtectedRoute>} />
        <Route path="/TermsOfUse" element={<TermsOfUse />} />
        <Route path="/privacidade" element={<Privacy />} />
        <Route path="/chamar-entregador" element={<ProtectedRoute requireMerchant><BlockedGate><CallCourier /></BlockedGate></ProtectedRoute>} />
        <Route path="/rastreio/:orderId" element={<CustomerTracking />} />
        {/* Sem BlockedGate: é histórico só-leitura, a loja bloqueada continua podendo consultar. */}
        <Route path="/users/:uid/pedidos" element={<ProtectedRoute><PedidosList /></ProtectedRoute>} />
        <Route path="/admin/lojas" element={<ProtectedRoute requireAdmin><AdminStores /></ProtectedRoute>} />
      </Routes>
      </Suspense>
      </ErrorBoundary>
    </>
  );
};

export default App;
